/*
 * Klein pickup calendar - customer-facing version v5, 2026-10-09.
 * V5 changes presentation only; V4 address and checkout checks remain enabled.
 * Technical error details are never rendered in the customer interface.
 * V4: branch changes add a new temporary address BEFORE removing our old one.
 * No in-place address-detail updates; strict read-back and rollback retained.
 * V3 Hesse normalization remains restricted to the exact pickup addresses.
 * Explicit selected/unselected queries; ownership checks and rollback retained.
 * No customer, company, order or payment mutations.
 * Uses ONLY an existing cart. Never creates or rebuilds a cart.
 * Cart credentials are kept in memory, never logged or persisted.
 * Default activation is limited to a test tab (section setting: test).
 */
(function (w) {
  'use strict';
  const API = '/api/2026-07/graphql.json';
  const PREFIX = 'klein.pickup-address.v1.';
  const TEST_KEY = 'klein.pickup-address.test';
  const PARAM = 'klein_abholadresse';
  const ADDRESS_FIELDS = `id selected oneTimeUse address { ... on CartDeliveryAddress {
    firstName lastName phone company address1 address2 city provinceCode zip countryCode
  } }`;
  // Query both states explicitly; never rely on the default selected filter.
  const FIELDS = `id totalQuantity buyerIdentity { purchasingCompany { location { id } } }
    delivery {
      selectedAddresses: addresses(selected: true) { ${ADDRESS_FIELDS} }
      unselectedAddresses: addresses(selected: false) { ${ADDRESS_FIELDS} }
    }`;
  const READ = `query KleinPickupAddressRead($cartId: ID!) { cart(id: $cartId) { ${FIELDS} } }`;
  const ADD = `mutation KleinPickupAddressAdd($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
    cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
      userErrors { field message code } warnings { code } cart { ${FIELDS} }
    }
  }`;
  const UPDATE = `mutation KleinPickupAddressUpdate($cartId: ID!, $addresses: [CartSelectableAddressUpdateInput!]!) {
    cartDeliveryAddressesUpdate(cartId: $cartId, addresses: $addresses) {
      userErrors { field message code } warnings { code } cart { ${FIELDS} }
    }
  }`;
  const REMOVE = `mutation KleinPickupAddressRemove($cartId: ID!, $addressIds: [ID!]!) {
    cartDeliveryAddressesRemove(cartId: $cartId, addressIds: $addressIds) {
      userErrors { field message code } warnings { code } cart { ${FIELDS} }
    }
  }`;
  const KEYS = ['firstName', 'lastName', 'phone', 'company', 'address1', 'address2', 'city', 'provinceCode', 'zip', 'countryCode'];
  const text = v => String(v == null ? '' : v).trim();
  const canonical = a => JSON.stringify(KEYS.map(k => text(a?.[k])));
  // Keep canonical() unchanged: older test records use its hashes for rollback.
  // Essential address fields must match. The narrowly scoped province exception is below.
  function comparisonValue(key, value) {
    let result = text(value).normalize('NFC').replace(/\s+/g, ' ');
    if (key === 'countryCode' || key === 'provinceCode') return result.toUpperCase();
    if (key === 'phone') return result.replace(/[\s().\/-]/g, '').replace(/^00/, '+');
    return result;
  }
  // Only these two configured German pickup addresses may omit the optional region.
  // This is not a general exemption for arbitrary shipping addresses or countries.
  const PICKUP_REGION_ADDRESSES = [
    {address1: 'Limburger Str. 7', zip: '61476', city: 'Kronberg im Taunus'},
    {address1: 'Vorstadt 9', zip: '61440', city: 'Oberursel (Taunus)'}
  ];
  function knownPickupAddress(address) {
    return comparisonValue('countryCode', address?.countryCode) === 'DE' &&
      PICKUP_REGION_ADDRESSES.some(branch => ['address1', 'zip', 'city'].every(
        key => comparisonValue(key, address?.[key]) === comparisonValue(key, branch[key])
      ));
  }
  function hesseCode(value) {
    const region = comparisonValue('provinceCode', value);
    return ['HE', 'DE-HE', 'HESSEN', 'HESSE'].includes(region) ? 'HE' : region;
  }
  function differentFields(actual, expected) {
    const mismatch = KEYS.filter(key =>
      comparisonValue(key, actual?.[key]) !== comparisonValue(key, expected?.[key])
    );
    // Do not relax any other field, or accept a different nonempty German region.
    // A missing region is allowed only when every other field matches this branch.
    if (mismatch.length === 1 && mismatch[0] === 'provinceCode' &&
        knownPickupAddress(expected) && knownPickupAddress(actual) &&
        hesseCode(expected.provinceCode) === 'HE' &&
        ['', 'HE'].includes(hesseCode(actual.provinceCode))) return [];
    return mismatch;
  }
  async function matchesRecordedAddress(address, record) {
    const hashes = [record?.targetHash, record?.lastHash].filter(Boolean);
    if (hashes.includes(await digest(canonical(address)))) return true;
    // Preserve V1/V2 rollback records. Reconstruct ONLY the region spelling;
    // every other stored field must still produce the original exact hash.
    if (!knownPickupAddress(address) || !['', 'HE'].includes(hesseCode(address.provinceCode))) return false;
    for (const provinceCode of ['', 'HE', 'DE-HE', 'Hessen', 'Hesse', 'HESSEN', 'HESSE']) {
      if (hashes.includes(await digest(canonical({...address, provinceCode})))) return true;
    }
    return false;
  }
  const sameAddress = (a, b) => differentFields(a, b).length === 0;
  const FIELD_NAMES = {
    firstName: 'Vorname', lastName: 'Nachname', phone: 'Telefon', company: 'Firma',
    address1: 'Strasse/Hausnummer', address2: 'Adresszusatz', city: 'Ort',
    provinceCode: 'Bundesland', zip: 'Postleitzahl', countryCode: 'Land'
  };
  function branchDescription(address) {
    if (!address) return 'keine lesbare Anschrift';
    if (comparisonValue('countryCode', address.countryCode) !== 'DE') return 'andere oder unvollstaendige Anschrift';
    const branch = PICKUP_REGION_ADDRESSES.find(item => ['address1', 'zip', 'city'].every(
      key => comparisonValue(key, address[key]) === comparisonValue(key, item[key])
    ));
    if (!branch) return 'andere oder unvollstaendige Anschrift';
    return branch.zip === '61476' ? 'Kronberg (Limburger Str. 7, 61476)' : 'Oberursel (Vorstadt 9, 61440)';
  }
  function addressDiagnostic(cart, target) {
    const selected = addresses(cart).find(a => a.selected === true);
    const warning = lastWarningCodes.length ? ' Shopify-Hinweise: ' + lastWarningCodes.join(', ') + '.' : '';
    return ' Erwartet: ' + branchDescription(target) + '. Zurueckgemeldet: ' + branchDescription(selected?.address) + '.' + warning;
  }
  function selectionProblem(cart, record, target, expectedCompany, isB2B) {
    const selected = addresses(cart).filter(a => a.selected === true);
    if (isB2B && cart.buyerIdentity?.purchasingCompany?.location?.id !== expectedCompany) {
      return '[A05] Die B2B-Zuordnung hat sich geaendert. Der Checkout bleibt aus Sicherheitsgruenden gesperrt.';
    }
    if (selected.length !== 1) {
      return '[A01] Shopify liefert ' + selected.length + ' ausgewaehlte Lieferadressen statt einer. Die Adressuebernahme ist noch nicht bestaetigt.';
    }
    if (selected[0].id !== record.ownedId) {
      return '[A02] Shopify hat eine andere Lieferadresse ausgewaehlt, nicht die Filialadresse dieses Tests.';
    }
    if (selected[0].oneTimeUse !== true) {
      return '[A03] Shopify bestaetigt die Filialadresse nicht als einmalige Lieferadresse.';
    }
    const mismatch = differentFields(selected[0].address, target);
    if (mismatch.length) {
      const regionDetail = mismatch.includes('provinceCode')
        ? ' Bundesland: erwartet ' + JSON.stringify(text(target.provinceCode)) +
          ', erhalten ' + JSON.stringify(text(selected[0].address?.provinceCode)) + '.'
        : '';
      return '[A04] Abweichende Adressfelder: ' + mismatch.map(k => FIELD_NAMES[k]).join(', ') +
        '.' + regionDetail + addressDiagnostic(cart, target) + ' Die Filialadresse ist noch nicht sicher bestaetigt.';
    }
    return '';
  }

  function failure(message) {
    const e = new Error(message);
    e.pickupAddress = true;
    return e;
  }
  function storage() {
    try {
      const k = PREFIX + 'probe';
      w.localStorage.setItem(k, '1');
      w.localStorage.removeItem(k);
      return w.localStorage;
    } catch (_) {
      throw failure('Die Adress\u00fcbernahme ben\u00f6tigt Browserspeicher f\u00fcr die sichere R\u00fccksetzung. Bitte diesen erlauben oder den Adresstest ausschalten.');
    }
  }
  function hasRecords() {
    try {
      for (let n = 0; n < w.localStorage.length; n++) {
        if (w.localStorage.key(n)?.startsWith(PREFIX)) return true;
      }
    } catch (_) { /* No changes are made without working storage. */ }
    return false;
  }
  function testEnabled() {
    try {
      const flag = new URL(w.location.href).searchParams.get(PARAM);
      if (flag === '1' || flag === '0') w.sessionStorage.setItem(TEST_KEY, flag);
      return w.sessionStorage.getItem(TEST_KEY) === '1';
    } catch (_) { return false; }
  }
  function enabled(data) {
    if (data?.pickupActive === false) return false;
    const mode = data?.config?.addressTransferMode || 'off';
    return mode === 'on' || (mode === 'test' && testEnabled());
  }
  async function digest(value) {
    if (!w.crypto?.subtle) throw failure('Die sichere Adress\u00fcbernahme ist in diesem Browser nicht verf\u00fcgbar. Bitte den Adresstest ausschalten.');
    const bytes = await w.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), x => x.toString(16).padStart(2, '0')).join('');
  }
  let lastWarningCodes = [];
  async function request(query, variables, payloadName) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await w.fetch(API, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({query, variables}), signal: controller.signal
      });
      if (!response.ok) throw failure('Shopify hat die Adressanfrage nicht angenommen. Bitte erneut versuchen oder den Adresstest ausschalten.');
      const result = await response.json();
      if (result.errors?.length) throw failure('Die Adressschnittstelle ist f\u00fcr diesen Warenkorb nicht verf\u00fcgbar. Der Checkout wurde nicht ge\u00f6ffnet. Bitte den Adresstest ausschalten.');
      const payload = payloadName ? result.data?.[payloadName] : result.data;
      if (payloadName) lastWarningCodes = (payload?.warnings || []).map(x => text(x.code).replace(/[^A-Z0-9_]/g, '')).filter(Boolean);
      if (payload?.userErrors?.length) {
        const code = text(payload.userErrors[0].code).replace(/[^A-Z0-9_]/g, '');
        throw failure('Shopify konnte die Filialadresse nicht ausw\u00e4hlen' + (code ? ' (' + code + ')' : '') + '. Bei B2B bitte die einmalige Lieferadresse pr\u00fcfen.');
      }
      const cart = payload?.cart;
      if (!cart) throw failure('Dieser bestehende Warenkorb l\u00e4sst sich nicht \u00fcber die Adressschnittstelle bearbeiten. Es wurde kein Ersatzwarenkorb angelegt. Bitte den Adresstest ausschalten.');
      return cart;
    } catch (e) {
      if (e.pickupAddress) throw e;
      throw failure('Die Adress\u00fcbernahme konnte nicht best\u00e4tigt werden. Bitte erneut versuchen. Vor dem Weiterbestellen den Adresstest gegebenenfalls ausschalten.');
    } finally { clearTimeout(timer); }
  }
  function addresses(cart) {
    const delivery = cart?.delivery;
    if (!delivery) return [];
    const unique = new Map();
    // selected=true is the authority for the pre-selected address.
    // Some responses can include it in both lists, so merge by ID.
    for (const entry of [...(delivery.selectedAddresses || []), ...(delivery.unselectedAddresses || [])]) {
      if (entry?.id && !unique.has(entry.id)) unique.set(entry.id, entry);
    }
    return [...unique.values()];
  }
  const readCart = cartId => request(READ, {cartId});
  const mutate = (query, variables, name) => request(query, variables, name);
  const companyGid = id => !id ? '' : text(id).startsWith('gid://') ? text(id) : 'gid://shopify/CompanyLocation/' + text(id);
  let serial = Promise.resolve();
  function runLocked(key, fn) {
    const task = () => w.navigator?.locks?.request
      ? w.navigator.locks.request(key, {mode: 'exclusive'}, fn) : fn();
    const result = serial.catch(() => {}).then(task);
    serial = result.catch(() => {});
    return result;
  }
  function loadRecord(s, key) {
    const raw = s.getItem(key);
    if (!raw) return null;
    try {
      const r = JSON.parse(raw);
      if (r.version !== 1 || !Array.isArray(r.beforeIds)) throw new Error('invalid');
      return r;
    } catch (_) { throw failure('Die R\u00fccksetzungsdaten des Adresstests sind unvollst\u00e4ndig. Bitte nicht mit einer ungepr\u00fcften Lieferadresse bestellen.'); }
  }
  function persist(s, key, record) {
    try { s.setItem(key, JSON.stringify(record)); }
    catch (_) { throw failure('Die Adress\u00e4nderung wurde aus Sicherheitsgr\u00fcnden gestoppt: R\u00fccksetzungsdaten konnten nicht gespeichert werden.'); }
  }
  async function ownedAddress(cart, record) {
    if (!record) return null;
    if (record.ownedId) return addresses(cart).find(a => a.id === record.ownedId) || null;
    // Recover an Add whose response was interrupted; only newly created addresses qualify.
    const candidates = [];
    for (const a of addresses(cart)) {
      if (!record.beforeIds.includes(a.id) && a.oneTimeUse && await matchesRecordedAddress(a.address, {targetHash: record.targetHash})) candidates.push(a);
    }
    if (candidates.length > 1) throw failure('Die Testadresse ist nicht eindeutig zuordenbar. Bitte die Lieferadresse im Checkout pr\u00fcfen.');
    return candidates[0] || null;
  }
  function retirementList(record) {
    if (!record.retired) record.retired = [];
    if (!Array.isArray(record.retired) || record.retired.some(x => !x?.id || !x.targetHash)) {
      throw failure('[R04] Die Ruecksetzungsdaten des Filialwechsels sind unvollstaendig. Bitte den Test stoppen.');
    }
    return record.retired;
  }
  async function retireOldAddresses(cartId, cart, s, key, record) {
    const retired = retirementList(record);
    if (!retired.length) return cart;
    const removeIds = [];
    for (const old of retired) {
      const entry = addresses(cart).find(a => a.id === old.id);
      if (!entry) continue;
      if (entry.id === record.ownedId || entry.selected === true || entry.oneTimeUse !== true ||
          !(await matchesRecordedAddress(entry.address, old))) {
        throw failure('[R05] Die bisherige Testadresse kann nicht sicher entfernt werden. Es wurde keine fremde Adresse geloescht.');
      }
      removeIds.push(entry.id);
    }
    if (removeIds.length) {
      await mutate(REMOVE, {cartId, addressIds: removeIds}, 'cartDeliveryAddressesRemove');
      cart = await readCart(cartId);
      if (addresses(cart).some(a => removeIds.includes(a.id))) {
        throw failure('[R02] Die bisherige Testadresse ist noch im Warenkorb. Bitte erneut pruefen.');
      }
    }
    record.retired = [];
    persist(s, key, record);
    return cart;
  }
  async function restore(cartId, cart, s, key, record) {
    if (!record) return;
    const own = await ownedAddress(cart, record);
    // An interrupted Add must not result in deleting an unowned address.
    if (record.addStarted && !record.ownedId && !own) {
      const unknown = addresses(cart).some(a => !record.beforeIds.includes(a.id));
      if (unknown) throw failure('[R06] Ein unterbrochener Filialwechsel hat eine noch nicht zuordenbare Adresse hinterlassen. Bitte die Anschrift im Checkout pruefen; sie wurde nicht geloescht.');
    }
    const tracked = [...retirementList(record)];
    if (own) tracked.push({id: own.id, targetHash: record.targetHash, lastHash: record.lastHash});
    const removeIds = [], selectedBefore = addresses(cart).filter(a => a.selected === true);
    // Validate every tracked address before making any removal request.
    for (const saved of tracked) {
      const entry = addresses(cart).find(a => a.id === saved.id);
      if (!entry || removeIds.includes(entry.id)) continue;
      if (entry.oneTimeUse !== true || !(await matchesRecordedAddress(entry.address, saved))) {
        throw failure('[R01] Eine Testadresse wurde zwischenzeitlich veraendert. Sie wurde nicht geloescht. Bitte die Lieferadresse pruefen.');
      }
      removeIds.push(entry.id);
    }
    if (removeIds.length) {
      await mutate(REMOVE, {cartId, addressIds: removeIds}, 'cartDeliveryAddressesRemove');
      cart = await readCart(cartId);
      if (addresses(cart).some(a => removeIds.includes(a.id))) throw failure('[R02] Eine Testadresse ist noch im Warenkorb. Bitte die Ruecksetzung erneut versuchen.');
    }
    const currentSelected = addresses(cart).filter(a => a.selected === true);
    if ((selectedBefore.some(a => removeIds.includes(a.id)) || !currentSelected.length) &&
        record.previousSelectedId && addresses(cart).some(a => a.id === record.previousSelectedId)) {
      await mutate(UPDATE, {cartId, addresses: [{id: record.previousSelectedId, selected: true}]}, 'cartDeliveryAddressesUpdate');
      cart = await readCart(cartId);
      const restored = addresses(cart).filter(a => a.selected === true);
      if (restored.length !== 1 || restored[0].id !== record.previousSelectedId) throw failure('[R03] Die bisherige Lieferadresse konnte nicht wieder ausgewaehlt werden. Bitte erneut pruefen.');
    }
    s.removeItem(key);
  }
  async function syncImpl(nativeCart, data, choice, stillCurrent, forceOff) {
    const active = !forceOff && enabled(data);
    if (!active && !hasRecords()) return {active: false};
    const token = text(nativeCart?.token);
    if (!token) throw failure('Die Warenkorbkennung fehlt. Bitte die Warenkorbseite neu laden.');
    const cartId = token.startsWith('gid://shopify/Cart/') ? token : 'gid://shopify/Cart/' + token;
    const key = PREFIX + await digest(cartId);
    const s = storage();
    return runLocked(key, async () => {
      if (!stillCurrent()) return {active, stale: true};
      let record = loadRecord(s, key);
      if (!active && !record) return {active: false};
      let cart = await readCart(cartId);
      if (!active || !nativeCart.item_count) {
        await restore(cartId, cart, s, key, record);
        return {active: false};
      }
      const expectedCompany = companyGid(data.buyer?.companyLocationId);
      const assertCompany = value => {
        if (data.buyer?.isB2B && (!expectedCompany || value.buyerIdentity?.purchasingCompany?.location?.id !== expectedCompany)) {
          throw failure('[A05] Die B2B-Zuordnung konnte nicht bestaetigt werden. Es wird kein Ersatzwarenkorb angelegt.');
        }
      };
      assertCompany(cart);
      const location = data.config.locations.find(l => text(l.id) === text(choice.location));
      const branch = location?.shippingAddress;
      if (!branch?.address1 || !branch.zip || !branch.city || branch.countryCode !== 'DE') throw failure('Die ausgewaehlte Filiale hat keine vollstaendige Lieferanschrift.');
      const selected = addresses(cart).find(a => a.selected === true)?.address;
      const target = {
        firstName: text(data.buyer?.firstName) || text(selected?.firstName),
        lastName: text(data.buyer?.lastName) || text(selected?.lastName),
        phone: text(data.buyer?.phone) || text(selected?.phone),
        company: 'Metzgerei Klein GmbH',
        address1: text(branch.address1), address2: text(branch.address2),
        zip: text(branch.zip), city: text(branch.city),
        provinceCode: text(branch.provinceCode), countryCode: 'DE'
      };
      if (!target.lastName) throw failure('Der Kundenname fehlt fuer die Lieferadresse. Bitte die Kundendaten pruefen.');
      let own = await ownedAddress(cart, record);
      // Recover an interrupted Add using only the stored fingerprint and before-IDs.
      // Never issue repeated Adds just because the previous response was uncertain.
      if (record?.addStarted && !own && !record.ownedId) {
        for (const delay of [250, 750]) {
          await new Promise(resolve => setTimeout(resolve, delay));
          if (!stillCurrent()) return {active: true, stale: true};
          cart = await readCart(cartId); assertCompany(cart);
          own = await ownedAddress(cart, record);
          if (own) break;
        }
        if (!own) throw failure('[W01] Die neue Filialadresse wurde nicht bestaetigt. Es wird keine weitere Adresse auf Verdacht angelegt.' + addressDiagnostic(cart, target));
      }
      if (record?.ownedId && !own) throw failure('[W02] Die bisherige Testadresse ist nicht mehr lesbar. Bitte den Adresstest ausschalten und den Warenkorb pruefen.');
      if (own && (own.oneTimeUse !== true || !(await matchesRecordedAddress(own.address, record)))) {
        throw failure('[R01] Die Testadresse wurde ausserhalb des Kalenders veraendert. Sie wurde nicht ueberschrieben. Bitte den Test stoppen.');
      }
      if (own && !record.ownedId) {record.ownedId = own.id; record.addStarted = false; persist(s, key, record);}
      // Finish a previously interrupted transition before starting another one.
      // This read already established the current address fingerprint and company.
      if (own && retirementList(record).length && !sameAddress(own.address, target)) {
        if (!stillCurrent()) return {active: true, stale: true};
        if (own.selected !== true) throw failure('[R07] Der vorherige Filialwechsel ist noch nicht bestaetigt. Bitte den Adresstest zuruecksetzen.');
        cart = await retireOldAddresses(cartId, cart, s, key, record);
        assertCompany(cart);
        own = addresses(cart).find(a => a.id === record.ownedId);
        if (!own || !own.selected || !own.oneTimeUse || !(await matchesRecordedAddress(own.address, record))) {
          throw failure('[R07] Die Auswahl hat sich waehrend des Filialwechsels geaendert. Bitte erneut pruefen.');
        }
      }
      if (!record) {
        record = {version: 1, beforeIds: addresses(cart).map(a => a.id), previousSelectedId: addresses(cart).find(a => a.selected === true)?.id || '', ownedId: '', targetHash: '', lastHash: '', retired: [], addStarted: false};
      }
      const verifySelection = async () => {
        let problem = '';
        for (const delay of [0, 250, 750]) {
          if (delay) await new Promise(resolve => setTimeout(resolve, delay));
          if (!stillCurrent()) return false;
          cart = await readCart(cartId);
          problem = selectionProblem(cart, record, target, expectedCompany, data.buyer?.isB2B);
          if (!problem) return true;
        }
        throw failure(problem);
      };
      if (!stillCurrent()) return {active: true, stale: true};
      if (own && sameAddress(own.address, target)) {
        if (!own.selected) {
          // Select only: never try to rewrite the geographic fields in place.
          await mutate(UPDATE, {cartId, addresses: [{id: own.id, selected: true, oneTimeUse: true}]}, 'cartDeliveryAddressesUpdate');
        }
      } else {
        // Retain a verified record of the old temporary address before creating the new one.
        // The old address remains until the new branch has passed an independent read-back.
        if (own) {
          if (retirementList(record).length) throw failure('[R07] Ein vorheriger Filialwechsel ist noch nicht abgeschlossen. Bitte zuerst den Adresstest zuruecksetzen.');
          const oldHash = await digest(canonical(own.address));
          record.retired.push({id: own.id, targetHash: oldHash, lastHash: oldHash});
        }
        record.beforeIds = addresses(cart).map(a => a.id);
        record.ownedId = '';
        record.targetHash = await digest(canonical(target));
        record.lastHash = '';
        record.addStarted = true;
        persist(s, key, record); // Save recovery data BEFORE making the request.
        cart = await mutate(ADD, {cartId, addresses: [{selected: true, oneTimeUse: true, address: {deliveryAddress: target}}]}, 'cartDeliveryAddressesAdd');
        assertCompany(cart);
        const candidates = addresses(cart).filter(a => !record.beforeIds.includes(a.id) && a.oneTimeUse === true && sameAddress(a.address, target));
        // If mutation data lagged, retry READS only; do not add a second new address.
        if (candidates.length === 1) own = candidates[0];
        else own = await ownedAddress(cart, record);
        if (!own) {
          for (const delay of [250, 750]) {
            await new Promise(resolve => setTimeout(resolve, delay));
            if (!stillCurrent()) return {active: true, stale: true};
            cart = await readCart(cartId); assertCompany(cart);
            own = await ownedAddress(cart, record);
            if (own) break;
          }
        }
        if (!own) throw failure('[W01] Shopify hat keine neue passende Filialadresse zurueckgegeben. Die bisherige Adresse wurde nicht entfernt.' + addressDiagnostic(cart, target));
        record.ownedId = own.id;
        record.lastHash = await digest(canonical(own.address));
        record.addStarted = false;
        persist(s, key, record);
      }
      if (!(await verifySelection())) return {active: true, stale: true};
      const chosen = addresses(cart).find(a => a.id === record.ownedId);
      record.lastHash = await digest(canonical(chosen.address));
      persist(s, key, record);
      // Only our old, fingerprint-verified, unselected one-time address may be removed.
      const hadRetired = retirementList(record).length > 0;
      if (!stillCurrent()) return {active: true, stale: true};
      cart = await retireOldAddresses(cartId, cart, s, key, record);
      if (hadRetired && !(await verifySelection())) return {active: true, stale: true};
      return {active: true, verified: true, stale: !stillCurrent(), location: location.name};
    });
  }
  w.KleinPickupAddressBridge = {
    enabled, hasRecords,
    sync: (cart, data, choice, stillCurrent = () => true) => syncImpl(cart, data, choice, stillCurrent, false),
    restore: (cart, data = {}) => syncImpl(cart, data, {}, () => true, true)
  };
  // On leaving pickup, remove only the temporary address owned by this extension.
  // Customers who never activated address transfer execute no additional requests.
  async function cleanupOutsidePickup() {
    if (!hasRecords()) return;
    const host = w.document.querySelector('[data-klein-pickup-data]');
    if (host?.dataset.enabled === 'true' && w.KleinPickupEngine) return;
    const selector = 'button[name="checkout"],input[name="checkout"],a[href="/checkout"],a[href$="/checkout"]';
    const block = e => {
      if (e.target.closest?.(selector) || (e.type === 'submit' && e.submitter?.matches?.(selector))) { e.preventDefault(); e.stopImmediatePropagation(); }
    };
    w.document.addEventListener('click', block, true);
    w.document.addEventListener('submit', block, true);
    let notice;
    try {
      const root = w.Shopify?.routes?.root || '/';
      const response = await w.fetch(root + 'cart.js', {cache: 'no-store', credentials: 'same-origin'});
      if (!response.ok) throw new Error('cart');
      await w.KleinPickupAddressBridge.restore(await response.json());
      w.document.removeEventListener('click', block, true);
      w.document.removeEventListener('submit', block, true);
    } catch (_) {
      notice = w.document.createElement('div');
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'padding:16px;border:2px solid #8b201a;background:white;color:#111;position:relative;z-index:1000';
      notice.textContent = 'Die Bestellung kann gerade nicht fortgesetzt werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut. ';
      const retry = w.document.createElement('button');
      retry.type = 'button'; retry.textContent = 'Erneut versuchen';
      retry.addEventListener('click', () => w.location.reload()); notice.append(retry);
      w.document.body.prepend(notice);
    }
  }
  if (w.document) {
    if (w.document.readyState === 'loading') w.document.addEventListener('DOMContentLoaded', cleanupOutsidePickup, {once: true});
    else cleanupOutsidePickup();
  }
})(typeof window === 'undefined' ? globalThis : window);

(() => {
  'use strict';
  function boot(){
    const host=document.querySelector('[data-klein-pickup-data]');
    if(!host||host.dataset.enabled!=='true'||!window.KleinPickupEngine)return;
    window.KleinPickupCalendar?.destroy();
    const E=window.KleinPickupEngine,abort=new AbortController(),root=window.Shopify?.routes?.root||'/';
    const listen=(el,type,fn,opts={})=>el.addEventListener(type,fn,{...opts,signal:abort.signal});
    let data=JSON.parse(host.querySelector('[data-pickup-json]').textContent),c=data.config,t=data.text;
    let choice={location:data.attributes.AbholortID||'',date:data.attributes.Abholdatum||'',time:data.attributes.Abholzeit||''};
    let month=E.iso(E.berlin().day).slice(0,7),queue=Promise.resolve(),pending=0,error=false,destroyed=false,refreshTimer,refreshPromise,revision=0,status='',navigating=false;
    const checkoutSelector='button[name="checkout"],input[name="checkout"],a[href="/checkout"],a[href$="/checkout"]';
    const dateLabel=s=>new Intl.DateTimeFormat('de-DE',{timeZone:'UTC',weekday:'short',day:'2-digit',month:'2-digit',year:'numeric'}).format(E.date(s));
    const prep=()=>Math.max(0,...data.items.map(x=>x.days));
    const valid=()=>E.valid(choice,E.berlin(),prep(),c);
    const signature=()=>JSON.stringify(data.items.map(x=>[x.key,x.quantity,x.days]));
    const customerErrorText='Ihre Abholung konnte gerade nicht bestätigt werden. Bitte versuchen Sie es erneut.';
    const cleanPreparationText=value=>String(value||'').replace(/Au(?:\u00df|ss)erhalb\s+der\s+Produktionszeiten\s+l\u00e4uft\s+diese\s+Zeit\s+nicht\s+weiter\.\s*/gi,'').trim();
    const storeHandles={'118083715419':'filiale-kronberg-oberhochstadt','118190571867':'filiale-oberursel'};
    function headerLocation(){try {const h=localStorage.getItem('selected-store');return Object.keys(storeHandles).find(id=>storeHandles[id]===h)||'';}catch(_){return '';}}
    function syncHeader(){document.querySelectorAll('store-selector[data-main-selector]').forEach(el=>el.kleinSelectStore?.(storeHandles[choice.location]));}
    function preselect(){
      const before=JSON.stringify(choice);
      if(!c.locations.some(l=>l.id===choice.location))choice.location=c.locations[0]?.id||'';
      const days=selectedDates(),day=days.find(d=>d.date===choice.date)||days[0];
      choice.date=day?.date||'';
      if(!day?.slots.some(s=>s.join('–')===choice.time))choice.time=day?.slots[0]?.join('–')||'';
      return before!==JSON.stringify(choice);
    }
    function attrs(){
      const l=c.locations.find(x=>x.id===choice.location),ok=valid();
      return {'AbholortID':l?.id||'','Abholort':l?.name||'','Abholdatum':ok?choice.date:'','Abholzeit':ok?choice.time:'','Wunschabholtermin':ok?`${dateLabel(choice.date)}, ${choice.time}`:'','Order Fulfillment Type':'Pickup','Order Location':l?.name||'','Order Location Address':l?.address||'','Order Due Date':ok?choice.date:'','Translated Order Due Date':ok?dateLabel(choice.date):'','Order Due Time':ok?choice.time:'','Translated Order Due Time':ok?choice.time:'','Wunschliefertag':'','Lieferdatum':'','Versanddatum':'','Lieferterminart':''};
    }
    function save(){
      const version=++revision,values=attrs();pending++;error=false;status=t.saving;render();
      queue=queue.catch(()=>{}).then(async()=>{
        if(destroyed||version!==revision)return;
        const res=await fetch(root+'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({attributes:values}),signal:abort.signal});
        if(!res.ok)throw new Error('save');const cart=await res.json();
        if(Object.entries(values).some(([k,v])=>(cart.attributes?.[k]||'')!==v))throw new Error('verify');
        const addressResult=await window.KleinPickupAddressBridge.sync(cart,data,{location:values.AbholortID},()=>!destroyed&&version===revision);
        if(version===revision){data.attributes=cart.attributes||{};status=valid()?'':t.choose;}
      }).catch(e=>{if(!destroyed&&version===revision){error=true;status=customerErrorText;}}).finally(()=>{pending--;if(!destroyed)render();});
      return queue;
    }
    function selectedDates(){const loc=c.locations.find(x=>x.id===choice.location);return loc?E.available(E.berlin(),prep(),loc,c):[];}
    function invalidate(){
      if(!choice.date)return false;
      const day=selectedDates().find(x=>x.date===choice.date);
      if(!day||(choice.time&&!day.slots.some(x=>x.join('–')===choice.time))){choice.date='';choice.time='';status=t.changed;return true;}
      return false;
    }
    function mount(){
      document.querySelectorAll(checkoutSelector).forEach(button=>{
        const target=button.closest('.flex-buttons')||button,parent=target.parentElement;
        if(parent?.querySelector(':scope > [data-klein-pickup]'))return;
        if(!parent||button.closest('[data-klein-pickup]'))return;
        const fragment=host.querySelector('template').content.cloneNode(true),widget=fragment.querySelector('[data-klein-pickup]');
        const id='pickup-'+Math.random().toString(36).slice(2);
        widget.querySelector('[data-location]').id=id+'-location';widget.querySelector('[data-location-label]').htmlFor=id+'-location';
        widget.querySelector('[data-time]').id=id+'-time';widget.querySelector('[data-time-label]').htmlFor=id+'-time';
        c.locations.forEach(l=>{const option=document.createElement('option');option.value=l.id;option.textContent=l.name;widget.querySelector('[data-location]').append(option);});
        parent.insertBefore(fragment,target);
        listen(widget,'change',event=>{
          if(event.target.matches('[data-location]')){choice.location=event.target.value;preselect();syncHeader();month=(choice.date||E.iso(E.berlin().day)).slice(0,7);save();}
          if(event.target.matches('[data-time]')){choice.time=event.target.value;save();}
        });
        listen(widget,'click',event=>{
          const day=event.target.closest('[data-date]');if(day&&!day.disabled){choice.date=day.dataset.date;preselect();save();}
          const nav=event.target.closest('[data-month]');if(nav){const d=E.date(month+'-01');d.setUTCMonth(d.getUTCMonth()+Number(nav.dataset.month));month=E.iso(d).slice(0,7);render();}
          if(event.target.closest('[data-retry]'))save();
        });
      });render();
    }
    function render(){
      const days=selectedDates(),byDate=new Map(days.map(x=>[x.date,x.slots])),location=c.locations.find(x=>x.id===choice.location);
      document.querySelectorAll('[data-klein-pickup]').forEach(w=>{
        w.hidden=!data.items.length;w.setAttribute('aria-busy',String(pending>0));w.querySelector('[data-location]').value=choice.location;
        const locationName=w.querySelector('[data-location-name]');if(locationName)locationName.textContent=location?location.name:'';
        const addressInfo=w.querySelector('[data-pickup-address]');if(addressInfo){addressInfo.hidden=true;addressInfo.textContent='';}
        w.querySelector('[data-calendar]').hidden=!location;
        const earliest=w.querySelector('[data-earliest]');earliest.textContent=location?(days.length?`${t.earliest} ${dateLabel(days[0].date)}, ${days[0].slots[0].join('–')} ${t.clock}`:t.none):t.locationFirst;
        const prepText=prep()?t.production.replace('{days}',prep()):t.basic.replace('{hours}',(location?.prepMinutes||0)/60);
        w.querySelector('[data-preparation]').textContent=location?cleanPreparationText(prepText):'';
        const d=E.date(month+'-01'),grid=w.querySelector('[data-days]');grid.replaceChildren();
        w.querySelector('[data-month-label]').textContent=new Intl.DateTimeFormat('de-DE',{timeZone:'UTC',month:'long',year:'numeric'}).format(d);
        const firstMonth=E.iso(E.berlin().day).slice(0,7),lastMonth=E.iso(E.add(E.berlin().day,c.horizonDays-1)).slice(0,7);
        w.querySelector('[data-month="-1"]').disabled=month<=firstMonth;w.querySelector('[data-month="1"]').disabled=month>=lastMonth;
        for(let i=0;i<(d.getUTCDay()+6)%7;i++)grid.append(document.createElement('span'));
        const end=new Date(Date.UTC(d.getUTCFullYear(),d.getUTCMonth()+1,0,12)).getUTCDate();
        for(let i=1;i<=end;i++){const key=month+'-'+String(i).padStart(2,'0'),b=document.createElement('button');b.type='button';b.className='klein-pickup__day';b.textContent=i;b.dataset.date=key;b.disabled=!byDate.has(key);b.setAttribute('aria-label',dateLabel(key)+(b.disabled?' – '+t.unavailable:''));b.setAttribute('aria-pressed',String(choice.date===key));grid.append(b);}
        const times=w.querySelector('[data-time]');times.replaceChildren();const blank=document.createElement('option');blank.value='';blank.textContent=t.selectTime;times.append(blank);
        (byDate.get(choice.date)||[]).forEach(slot=>{const o=document.createElement('option');o.value=slot.join('–');o.textContent=o.value+' '+t.clock;times.append(o);});times.value=choice.time;times.disabled=!choice.date;
        w.querySelector('[data-summary]').hidden=!valid();w.querySelector('[data-summary]').textContent=valid()?`${location.name} · ${dateLabel(choice.date)} · ${choice.time} ${t.clock}`:'';
        const statusNode=w.querySelector('[data-status]');statusNode.textContent=status;statusNode.dataset.error=String(error);statusNode.hidden=!status;w.querySelector('[data-retry]').hidden=!error;
      });
    }
    async function refresh(){
      if(refreshPromise)return refreshPromise;
      refreshPromise=(async()=>{
        const url=new URL(root,window.location.origin);url.searchParams.set('section_id',host.dataset.section);url.searchParams.set('_pickup',Date.now());
        const res=await fetch(url,{cache:'no-store',signal:abort.signal});if(!res.ok)throw new Error('refresh');
        const doc=new DOMParser().parseFromString(await res.text(),'text/html'),json=doc.querySelector('[data-pickup-json]');if(!json)throw new Error('data');
        const next=JSON.parse(json.textContent),before=signature();data=next;c=next.config;t=next.text;
        const changed=before!==signature();if(preselect()||changed)await save();mount();
      })().catch(e=>{if(!destroyed){error=true;status=customerErrorText;render();}throw e;}).finally(()=>{refreshPromise=null;});
      return refreshPromise;
    }
    function schedule(){clearTimeout(refreshTimer);refreshTimer=setTimeout(()=>refresh().catch(()=>{}),250);}
    async function guard(event){
      const target=event.target.closest?.(checkoutSelector),form=event.type==='submit'?event.target:null;
      if(!target&&!(form&&event.submitter?.matches?.(checkoutSelector)))return;
      event.preventDefault();event.stopImmediatePropagation();if(navigating)return;navigating=true;
      try{await queue;await refresh();await queue;if(!valid()){status=t.required;error=true;render();document.querySelector('[data-klein-pickup]')?.scrollIntoView({block:'center',behavior:'smooth'});return;}
        await save();if(error||pending)return;window.location.assign(root+'checkout');
      }catch(e){status=customerErrorText;error=true;render();}finally{navigating=false;}
    }
    document.documentElement.classList.add('klein-pickup-active');
    listen(document,'click',guard,{capture:true});listen(document,'submit',guard,{capture:true});
    for(const event of ['cart-updated','krown:cart:update','add-to-cart'])listen(document,event,schedule);
    listen(document,'change',event=>{if(!event.target.closest('[data-klein-pickup]')&&event.target.closest('cart-form,#cart,#site-cart-sidebar'))schedule();});
    listen(window,'pageshow',schedule);listen(document,'visibilitychange',()=>{if(!document.hidden)schedule();});
    function fromHeader(){const id=headerLocation();if(id&&id!==choice.location){choice.location=id;preselect();month=(choice.date||E.iso(E.berlin().day)).slice(0,7);save();}}
    listen(document,'storechanged',fromHeader,{capture:true});
    listen(window,'storage',event=>{if(event.key==='selected-store')fromHeader();});
    const observer=new MutationObserver(records=>{if(records.some(r=>!r.target.closest?.('[data-klein-pickup]')&&[...r.addedNodes,...r.removedNodes].some(n=>n.nodeType===1&&!n.matches('[data-klein-pickup]')&&(n.matches(checkoutSelector+', [data-js-cart-item]')||n.querySelector(checkoutSelector+', [data-js-cart-item]'))))){mount();schedule();}});
    observer.observe(document.body,{childList:true,subtree:true});
    const timer=setInterval(()=>{if(preselect())save();else render();},60000);
    window.KleinPickupCalendar={destroy(){destroyed=true;abort.abort();observer.disconnect();clearInterval(timer);clearTimeout(refreshTimer);document.querySelectorAll('[data-klein-pickup]').forEach(x=>x.remove());document.documentElement.classList.remove('klein-pickup-active');}};
    const original=JSON.stringify(choice),preferred=headerLocation();if(preferred)choice.location=preferred;preselect();month=(choice.date||E.iso(E.berlin().day)).slice(0,7);mount();syncHeader();if(original!==JSON.stringify(choice)||(data.items.length&&window.KleinPickupAddressBridge.enabled(data))||window.KleinPickupAddressBridge.hasRecords())save();
    customElements.whenDefined('store-selector').then(()=>{if(!destroyed)syncHeader();});
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
  document.addEventListener('shopify:section:load',boot);
})();
